import Checkbox from '@mui/material/Checkbox';
import * as yup from 'yup';
import consts from '../consts';
import Model from '../models/model';
import connection from '../services/Connection';
import util from '../util';
import CatAdminForm from '../views/forms/CatAdminForm';
import { useGridBase } from '../components/grid/GridBaseContext';

const rfidISA = "rfidisa", rfidRAER = "rfidraer";
const validateRFIDTagNumberFormat = () => {
	return {
		name: "validateRFIDTagNumberFormat",
		message: `Invalid RFID Tag Number - must be a valid hexadecimal`,
		test: (codes) => {
			for (const item of codes) {
				const codeType = item.CodeType?.replace(consts.regex.removeSpace, '').toLowerCase() || ''
				if (codeType === rfidRAER && (!item.RFIDEquipmentID || item.RFIDTagNumber)) {
					const tag = item.RFIDTagNumber || "";
					if (!tag || !consts.regex.hexValue.test(tag)) {
						return false;
					}
				}
			}
			return true;
		}
	}
}
const createDuplicateTest = (name, message, filterFn = null, key = 'Code') => ({
	name,
	test: function (codes) {
		const uniqueCodes = new Set();
		const codesToCheck = filterFn ? codes?.filter(filterFn) : codes;

		for (let i = 0, len = codesToCheck?.length; i < len; i++) {
			const code = (codesToCheck[i][key] || '').trim();
			if (!code) {
				return true
			}
			if (uniqueCodes.has(code)) {
				return false;
			}
			uniqueCodes.add(code);
		}
		return true;
	},
	message
});

// generic factory to create max-length tests for one or more keys
const maxLengthTest = ({ name, keys = ["Code"], max = 100, message = null }) => {
	return {
		name,
		test: function (codes) {
			if (!Array.isArray(codes)) return true; // let other validators handle type
			return codes.every(item => {
				// For each key: if it's missing/empty -> skip; otherwise check length
				return keys.every(k => {
					const val = item?.[k];
					// skip undefined / null / empty string
					if ([undefined, null, ''].includes(val)) return true;
					return val.length <= max;
				});
			});
		},
		message: message || `${keys.join(', ')} length should not be more than ${max}`
	};
}

export const syncFieldTests = {
	"DuplicateBarcodeFound": createDuplicateTest(
		"DuplicateBarcodeFound",
		'DuplicateBarcodeFound',
		(code) => code.CodeType === 'Barcode'
	),
	"AtLeastOneBarcodeisRequired": {
		name: 'AtLeastOneBarcodeisRequired',
		test: function (values) {
			const barcodes = values?.filter(item => item.CodeType === 'Barcode');
			return barcodes?.length > 0;
		},
		message: 'AtLeastOneBarcodeisRequired'
	},
	"AtLeastOneDefaultBardcodeisRequired": {
		name: 'AtLeastOneDefaultBardcodeisRequired',
		test: function (values) {
			const defaultBarcode = values?.filter((item) => (item.CodeType === "Barcode" && item.DefaultValue === true));
			return defaultBarcode?.length > 0;
		},
		message: 'At Least One Barcode Should be assigned as Default'
	},
	"OverDefaultBarcodeFound": {
		name: 'OverDefaultBarcodeFound',
		test: function (values) {
			const defaultBarcodeCount = values?.filter(item => item.DefaultValue === true && item.CodeType === "Barcode").length;
			const defaultRFIDCount = values?.filter(item => item.DefaultValue === true && item.CodeType === "RFID").length;

			return defaultBarcodeCount <= 1 && defaultRFIDCount <= 1;
		},
		message: 'OverDefaultBarcodeFound'
	},
	"CodeisRequired": {
		name: 'CodeisRequired',
		test: function (values, { parent }) {
			const isValid = Array.isArray(values) && values.every((item) => {
				const isRFIDCodeType = consts.rfidValidation.includes(item.CodeType);
				const isRFIDFieldRequired = parent.DeviceTypes?.some((type) => ["ISA", "RAER"].includes(type));
				if (isRFIDCodeType) {
					if (isRFIDFieldRequired) {
						const rfid = (item.RFIDTagNumber || item.RFIDEquipmentID || '')
						return rfid.length > 0;
					}
					return true;
				}
				const code = (item?.Code || '').trim();
				return code.length > 0;
			});

			return isValid;
		},
		message: ({ value }) => {
			return value.find(item => consts.rfidValidation.includes(item.CodeType) && !item.RFIDTagNumber)
				? 'RFIDIsRequired'
				: 'CodeisRequired'
		}
	},
	"CodeLengthMax100": maxLengthTest(
		{
			name: 'CodeLengthMax100',
			keys: ['Code']
		}
	),
	"DuplicateISARFIDFound": createDuplicateTest(
		"DuplicateISARFIDFound",
		"DuplicateRFIDTagNumber",
		(code) => code.CodeType.toUpperCase() === consts.codeTypeISA,
		"RFIDTagNumber"
	),
	"DuplicateRapidRFIDTagFound": createDuplicateTest(
		"DuplicateRapidRFIDTagFound",
		"DuplicateRFIDTagNumber",
		(code) => code.CodeType.toUpperCase() === consts.codeTypeRAER,
		"RFIDTagNumber"
	),
	"ValidateRFIDTagNumberHex": {
		name: 'ValidateRFIDTagNumberHex',
		message: 'RFIDTagNumber must be a valid decimal when CodeType is "RFID ISA"',
		test: (codes = []) => {
			for (const item of codes) {
				const codeType = item.CodeType?.replace(consts.regex.removeSpace, '').toLowerCase() || '';
				if (codeType === rfidISA && (!item.RFIDEquipmentID || item.RFIDTagNumber)) {
					const tag = item.RFIDTagNumber || '';
					if (tag && !consts.regex.decimalValue.test(tag)) {
						return false;
					}
				}
			}
			return true;
		},
	},
	"validateRFIDTagNumberFormat": validateRFIDTagNumberFormat,
	"PhysicianUsernameLengthMax20": {
		name: 'PhysicianUsernameLengthMax20',
		message: 'PhysicianUsernameMaxLength',
		test: function(value, { parent }) {
			const userType = parent?.UserType;
			return !(consts.regex.physicianAssistantRole.test(userType) && value && value.length > 20);
		}
	}
};

export const userLogColumns = [
	{ field: 'UserName', headerName: "UserName", width: 150 },
	{ field: 'Barcodes', headerName: "Barcodes", width: 150 },
	{ field: 'RFID', headerName: "RFID", width: 150 },
	{ field: 'status', headerName: "Status", width: 200 }
];

const { selectedUserSyncDeviceType, allUserSyncDeviceType, adminModuleUserType, PhysicianUserType, AssistantUserType } = consts;
const { isGridReadOnly, viewOnly, canAdd, canModify, deviceTypeRenderr, isFromTrackingSystem, isVisible } = util;

const onNewHideColumn = ({ formik }) => {
	return Boolean(formik?.values?.Id);
}

const CustomCheckBox = ({ row }) => {
	const { rowSelectionModel, onRowSelectionModel } = useGridBase();
	const handleOnClick = () => {
		const result = Array.from(rowSelectionModel.ids).find(item => item === row._id);
		if (result) {
			const updatedRowSelectionModel = Array.from(rowSelectionModel.ids).filter(item => item !== row._id);
			rowSelectionModel.ids = new Set(updatedRowSelectionModel);
			onRowSelectionModel(rowSelectionModel);
		}
		else {
			rowSelectionModel.ids.add(row._id);
			onRowSelectionModel(rowSelectionModel);
		}
	}

	const handleChecked = () => {
		return Array.from(rowSelectionModel.ids).some(item => item === row._id);
	}
	return (
		<Checkbox
			checked={handleChecked()}
			onClick={handleOnClick}
			disabled={viewOnly()}
			color="primary"
			value={row?.id}
			inputProps={{ 'aria-label': 'checkbox' }}
		/>
	)
}

const defaultUsername = ({ formik, values }) => {
	const { setFieldValue } = formik;
	if ((!formik.touched.UserName || values.UserName === '') && !values.id) {
		setFieldValue('UserName', `${values?.FirstName || ''}${values?.LastName || ''}`);
		if (values.UserName === '' && formik.touched.UserName) {
			formik.setFieldTouched('UserName', false);
		}
	}
}

export const fetchUserSyncDeviceType = async () => {
	const where = { CustomStringValue: { $exists: true, $in: allUserSyncDeviceType } }
	const formData = new FormData();
	formData.append('comboType', 'DeviceType');
	formData.append('CustomeWhere', JSON.stringify(where))
	const { options, count } = await fetch(connection.Combo, {
		method: 'POST',
		body: formData
	}).then((res) => res.json()).then((json) => {
		const { records } = json;
		let options = records?.map((record) => ({
			id: record.LookupId,
			label: record.DisplayValue
		}));
		if (!Array.isArray(options)) {
			options = [];
		}
		return { options: options || [], count: options?.length };
	});
	return { options, count };
}

const onCreateOption = (LookupTypeId) => (value) => {
	let action = 'save'
	let id = 0
	const formData = new FormData();
	formData.append('action', action);
	formData.append('id', id);
	formData.append('LookupTypeId', LookupTypeId);
	formData.append('CustomStringValue', value?.option?.id);
	formData.append('DisplayValue', value?.option?.id?.charAt(0)?.toUpperCase() + value?.option?.id?.slice(1));
	try {
		fetch(connection.EndoUserTypeLookup, {
			method: 'POST',
			body: formData,
			credentials: 'include'
		})
	} catch (error) {
		console.error('Error creating record:', error);
	}
};

const model = new Model({
	name: 'UserSync',
	title: 'UserSync',
	defaultSort: 'UserName ASC',
	// 42641 - add superadmin user type
	// Negating canModify for roles not allowed to add
	disableAdd: canAdd,
	// #42641 - add superadmin usertype
	// Making readOnly to true if roles has no access and if it's only readOnly
	readOnly: isGridReadOnly,
	disableDelete: true,
	// 42641 - add superadmin user type
	// Allowing only those that can modify to import
	allowImport: canModify,
	disablePrintBarcode: false,
	disableSelectAll: false,
	idProperty: '_id',
	isAddRemove: false,
	disableExport: false,
	customeErrorOnSubmit: true,
	showGridRefreshBtn: true,
	importProps: { message: "UserCountWarning", maxCount: consts.maxUserCount, logColumns: userLogColumns, fileType: consts.endoImportSpec.Users.fileType, type: consts.endoImportSpec.Users.type },
	FormExtension: ({ formik, activeRecord, modelConfig, isNew }) => {
		return (
			<CatAdminForm
				activeRecord={activeRecord}
				formik={formik}
				modelConfig={modelConfig}
				isItemSync={false}
				isNew={isNew}
			/>
		);
	},
	formGroups: [
		{
			fields: [
				'CreatedOn',
				'ModifiedOn',
				'LastResyncOn',
				'DataSource',
				'Codes',
				'UserUID',
				'UserName',
				'FirstName',
				'LastName',
				'UserType',
				'IsActive',
				'ReSync',
				'InterFacedYN',
				'EquipmentName',
				'DeviceTypes',
				'UserCodes'
			]
		}
	],
	customizeSaveData: (toSave, { values, activeRecord }) => {
		toSave.RemoveCodes = JSON.stringify(values?.RemoveCodes || []);
		toSave.Parameters = JSON.stringify(activeRecord?.record?.Parameters || []);
		toSave.UserCodes = JSON.stringify(values?.UserCodes || []);
		return toSave;
	},
	gridTitle: 'UserSync',
	columns: [
		{ header: 'UserId', field: 'UserUID', width: 120, hidden: true, isNotformfield: true },
		{ 
			field: 'UserName', 
			width: 120, 
			required: true, 
			inputProps: { autoFocus: false }, 
			readOnly: isFromTrackingSystem, 
			validate: yup.string().test(syncFieldTests.PhysicianUsernameLengthMax20)
		},
		{ field: 'FirstName', width: 120, onChange: defaultUsername, eager: true, readOnly: isFromTrackingSystem },
		{ field: 'LastName', width: 120, onChange: defaultUsername, eager: true, readOnly: isFromTrackingSystem },
		{ header: 'BarcodeCount', field: 'CodesCount', width: 120, isNotformfield: true, type: 'string', readOnly: isFromTrackingSystem, hidden: true },
		{ field: 'UserType', width: 120, lookup: 'EndoUserType', defaultValue: 'Operators',  onCreateOption: onCreateOption(consts.LookupType.EndoUserType), freeSolo: true, disabled: isFromTrackingSystem },
		{ header: 'Active', field: 'IsActive', type: 'bool', defaultValue: true, width: 120, readOnly: isFromTrackingSystem, onChange: ({ formik, value }) => { formik.setFieldValue('ReSync', value) } },
		{ header: 'DateDeActivated', field: 'DateDeActivated', type: 'date', width: 120, isLocal: true, format: 'MM/DD/YYYY hh:mm:ss A', hidden: true, readOnly: isFromTrackingSystem },
		{ field: 'ReSync', header: 'ReSync', type: 'bool', width: 100, hidden: true, inputWhen: onNewHideColumn, readOnly: isFromTrackingSystem, defaultValue: true },
		{ field: 'DataSource', width: 220, readOnly: true, inputWhen: onNewHideColumn, hidden: true },
		{ header: 'DefaultBarcode', field: 'DefaultBarcode', width: 200 },
		{ header: 'DefaultRFID', hidden: true, field: 'DefaultRFID', width: 200 },
		{
			header: 'updatedAt',
			field: 'ModifiedOn',
			width: 200,
			type: 'date',
			readOnly: true,
			format: 'MM/DD/YYYY hh:mm:ss A',
			inputWhen: onNewHideColumn,
			isLocal: true
		},
		{
			header: 'DateCreated',
			field: 'CreatedOn',
			width: 200,
			type: 'date',
			format: 'MM/DD/YYYY hh:mm:ss A',
			hidden: true,
			readOnly: true,
			isLocal: true,
			inputWhen: onNewHideColumn
		},
		{
			header: 'LastResyncOn',
			field: 'LastResyncOn',
			width: 120,
			type: 'date',
			hidden: true,
			readOnly: true,
			format: 'MM/DD/YYYY hh:mm:ss A',
			inputWhen: onNewHideColumn,
			isLocal: true
		},
		// TODO: Currently is not usable but infuture it can be use
		{
			header: 'Codes',
			field: 'UserCodes',
			width: 100,
			hidden: true,
			validate: yup
				.array()
				.of(
					yup.object().shape({
						CodeType: yup.string(),
						Code: yup.string(),
						IsActive: yup.boolean(),
						DefaultValue: yup.boolean(),
						RFIDTagNumber: yup.string(),
						RFIDEquipmentID: yup.string()
					})
				)
				.test(syncFieldTests.AtLeastOneBarcodeisRequired)
				.test(syncFieldTests.AtLeastOneDefaultBardcodeisRequired)
				.test(syncFieldTests.OverDefaultBarcodeFound)
				.test(syncFieldTests.CodeisRequired)
				.test(syncFieldTests.CodeLengthMax100)
				.test(syncFieldTests.DuplicateISARFIDFound)
				.test(syncFieldTests.DuplicateRapidRFIDTagFound)
				.test(syncFieldTests.validateRFIDTagNumberFormat()),
			excludeOnGrid: true,
			isNotExportField: true,
			isNotformfield: true
		},
		{
			header: 'Parameters',
			field: 'Parameters',
			width: 220,
			hidden: true,
			lookup: 'UserSyncParameters',
			type: 'object',
			validate: yup.array().of(
				yup.object().shape({
					Key: yup.string().required(),
					Value: yup.string()
				})
			),
			excludeOnGrid: true
		},
		{
			header: 'DeviceTypes',
			field: 'DeviceTypes',
			hidden: true,
			width: 120,
			lookup: fetchUserSyncDeviceType,
			defaultValue: selectedUserSyncDeviceType,
			multiple: true,
			type: 'object',
			valueGetter: deviceTypeRenderr,
			readOnly: isFromTrackingSystem,
			inputWhen: isVisible
		},
		{
			header: 'PrintBarcode',
			field: 'Print',
			type: 'action',
			filterable: false,
			width: 120,
			renderCell: CustomCheckBox,
			isNotExportField: true
		}
	],
	extraExportConfig: {
		PDF: {
			pageSize: 'A2'
		}
	}
});

export default model.createFormBase();
